import { ORGAN_MAP, TOUCH_POSITIONS } from "@/lib/dadDesign";
import { Link } from "react-router-dom";
import { Info } from "lucide-react";
import { OrganIcon } from "@/components/Shared";

// Stato di conoscenza DAD per area — display della proiezione census (PEND-PW-01).
// Label business-first (GO §7): UI LABEL ≠ CANONICAL ENUM (census_state resta verbatim).
export const CENS_STATES = {
  censita: { label: "Censita", color: "#10B981" },
  incompleta: { label: "Incompleta", color: "#F59E0B" },
  docs_mancanti: { label: "Documenti mancanti", color: "#FB923C" },
  anomalie: { label: "Da verificare", color: "#EF4444" },
  non_analizzata: { label: "Non ancora analizzata", color: "#6B7280" },
};

// Stato TECNICO di proiezione (fallback esplicito GO §11): NON è un sesto census state.
// Mai silent fallback alla semantica legacy.
export const SYNC_PENDING = { label: "Sincronizzazione in corso", color: "#38BDF8" };

// mapping canonico census_state (5 stati frozen spec §2) → chiave UI Censimento
export const CENSUS_TO_UI = {
  NON_ANALIZZATA: "non_analizzata",
  INCOMPLETA: "incompleta",
  DOCS_MANCANTI: "docs_mancanti",
  ANOMALIA: "anomalie",
  CENSITA: "censita",
};

// Micro-descrizioni funzionali per area (identità visiva di riferimento)
export const ORGAN_FEATURES = {
  mouth: ["Comunica e racconta", "Promuove e attrae", "Brand e reputazione"],
  brain: ["Comprende e collega", "Controlla e prevede", "Propone e coordina"],
  eyes_ears: ["Scan e App mobile", "Email, Drive e cloud", "Banca e POS"],
  memory: ["Archivio documentale", "Evidence Register", "Tracciabilità completa"],
  nervous_system: ["Trasporta le informazioni", "Automazioni intelligenti", "Collegamenti in tempo reale"],
  heart: ["Liquidità e flussi di cassa", "Pagamenti e incassi", "Previsioni finanziarie"],
  circulation: ["Movimenti economici", "IVA e imposte", "Collegamenti con i pilastri"],
  breathing: ["Ordini e clienti", "Fatturazione", "Produzione e servizi"],
  digestive: ["Fatture fornitori", "Gestione acquisti", "Controllo costi"],
  muscles: ["Collaboratori", "Payroll e stipendi", "Formazione e sviluppo"],
  skeleton: ["Contratti e documenti legali", "Struttura societaria", "Adempimenti istituzionali"],
  immune: ["Assicurazioni", "Controlli e scadenze", "Gestione dei rischi"],
  dna: ["Regole aziendali", "Personalizzazioni", "Processi specifici"],
};

// Posizioni hotspot sulla figura (% rispetto all'immagine dad-organism.jpg)
export const FIGURE_HOTSPOTS = Object.fromEntries(ORGAN_MAP.map(o => [o.id, {x:o.x,y:o.y}]));

// Lato dell'etichetta rispetto al dot (mobile): verso il centro del corpo
const LABEL_SIDE = {
  eyes_ears: "left", brain: "right", memory: "right", breathing: "left",
  heart: "left", circulation: "right", nervous_system: "left", digestive: "right",
  muscles: "right", immune: "left", dna: "right", skeleton: "right",
};

function Hotspot({ id, area, onOpen, showLabel }) {
  const st = CENS_STATES[area.state] || SYNC_PENDING;
  const p = FIGURE_HOTSPOTS[id];
  const biz = area.organ.business_name || area.organ.business_function;
  const side = LABEL_SIDE[id] || "right";
  return (
    <button
      type="button"
      data-testid={`cens-hotspot-${id}`}
      onClick={() => onOpen(id)}
      aria-label={biz}
      className="absolute -translate-x-1/2 -translate-y-1/2 group z-10 transition-transform active:scale-90"
      style={{ left: `${p.x}%`, top: `${p.y}%`, "--touch-x": `${TOUCH_POSITIONS[id][0]}%`, "--touch-y": `${TOUCH_POSITIONS[id][1]}%` }}
    >
      <span className="relative flex w-11 h-11 items-center justify-center">
        <span className="absolute inline-flex w-full h-full rounded-full animate-ping"
          style={{ background: st.color, opacity: 0.18 }} />
        <span className="absolute inline-flex w-6 h-6 rounded-full" style={{ background: `${st.color}26` }} />
        <span className="relative inline-flex w-2.5 h-2.5 rounded-full"
          style={{ background: st.color, boxShadow: `0 0 10px ${st.color}, 0 0 22px ${st.color}80` }} />
      </span>
      {showLabel ? (
        <span
          className={`pointer-events-none absolute top-1/2 -translate-y-1/2 flex items-center gap-1 whitespace-nowrap text-[9px] font-medium px-1.5 py-0.5 rounded-md ${
            side === "right" ? "left-full ml-0.5" : "right-full mr-0.5"
          }`}
          style={{ background: "rgba(11,17,32,0.82)", border: `1px solid ${st.color}40`, color: "#E5E7EB" }}>
          <span className="w-1 h-1 rounded-full shrink-0" style={{ background: st.color }} />
          {biz}
        </span>
      ) : (
        <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-7 hidden lg:group-hover:block whitespace-nowrap text-[10px] font-medium px-2 py-1 rounded-lg glass"
          style={{ color: st.color }}>
          {area.organ.anatomical_name || biz}
        </span>
      )}
    </button>
  );
}

// Figura anatomica protagonista con hotspot interattivi colorati per stato
export function OrganismFigure({ areas, onOpen, showLabel = false }) {
  const byId = Object.fromEntries(areas.map((a) => [a.organ.organ_id, a]));
  return (
    <div className="relative w-full" data-testid="cens-organism-figure">
      <div className="absolute inset-0 rounded-3xl pointer-events-none"
        style={{ boxShadow: "inset 0 0 80px rgba(0,229,255,0.06), 0 0 60px rgba(0,229,255,0.05)" }} />
      <img src="/assets/dad-organism.jpg" alt="L'azienda come un organismo"
        onLoad={() => window.dispatchEvent(new Event("resize"))}
        className="w-full h-auto rounded-3xl select-none" draggable={false}
        style={{ border: "1px solid rgba(0,229,255,0.12)" }} />
      <Link to="/marketing" className="dad-hotspot dad-census-mouth" style={{"--x":"50%","--y":"15%","--touch-x":"50%","--touch-y":"15%"}} aria-label="Bocca — Marketing & Comunicazione — Area in sviluppo"><span className="dad-hotspot-dot" /></Link>
      {Object.keys(FIGURE_HOTSPOTS).filter(id => id !== "mouth").map((id) =>
        byId[id] ? <Hotspot key={id} id={id} area={byId[id]} onOpen={onOpen} showLabel={showLabel} /> : null
      )}
    </div>
  );
}

// Card area business (colonne laterali desktop), collegata visivamente alla figura
export function AreaCard({ area, side, onShowBasis }) {
  const { organ, docsCount, state, coverage, qualified } = area;
  const st = CENS_STATES[state] || SYNC_PENDING;
  const feats = ORGAN_FEATURES[organ.organ_id] || [];
  return (
    <div className="relative">
      <Link to={organ.route} data-testid={`cens-area-${organ.organ_id}`}
        className="relative block rounded-2xl p-3 border border-white/5 hover:border-cyan-400/30 transition-colors"
        style={{ background: "rgba(11,17,32,0.55)" }}>
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.18)", color: "#00E5FF" }}>
            <OrganIcon icon={organ.icon} size={16} />
          </span>
          <div className="min-w-0">
            <div className="text-[13px] font-bold text-gray-100 leading-tight uppercase tracking-wide truncate">
              {organ.anatomical_name}
            </div>
            <div className="text-[10.5px] font-medium leading-tight" style={{ color: "#00E5FF" }}>
              {organ.business_name || organ.business_function}
            </div>
          </div>
        </div>
        {feats.length > 0 && (
          <ul className="mt-2.5 space-y-1 hidden xl:block">
            {feats.map((f) => (
              <li key={f} className="text-[11px] text-gray-400 flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full shrink-0" style={{ background: `${st.color}99` }} />
                {f}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-2.5 flex items-center gap-1.5 text-[10px] font-medium" style={{ color: st.color }}
          data-testid={`cens-area-state-${organ.organ_id}`}>
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: st.color, boxShadow: `0 0 6px ${st.color}` }} />
          {st.label}
          <span className="text-gray-500 font-normal">· {docsCount} documenti</span>
        </div>
        <div className="mt-1.5 flex items-center gap-1.5" data-testid={`cens-coverage-${organ.organ_id}`}>
          {coverage == null ? (
            <span className="text-[10px] text-gray-600">Copertura —</span>
          ) : (
            <>
              <div className="h-1 w-16 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                <div className="h-full rounded-full transition-[width] duration-500"
                  style={{ width: `${coverage}%`, background: st.color }} />
              </div>
              <span className="text-[10px] font-semibold text-gray-400">{Math.round(coverage)}%</span>
            </>
          )}
          {qualified && (
            <span className="text-[9px] font-medium text-amber-400" data-testid={`cens-qualified-${organ.organ_id}`}>
              qualificata
            </span>
          )}
        </div>
      </Link>
      <button type="button" aria-label="Perché questa percentuale?"
        data-testid={`cens-basis-open-${organ.organ_id}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShowBasis?.(organ.organ_id); }}
        className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center text-gray-500 hover:text-cyan-300 transition-colors z-10"
        style={{ background: "rgba(0,229,255,0.06)", border: "1px solid rgba(0,229,255,0.15)" }}>
        <Info size={11} />
      </button>
    </div>
  );
}

export const FLOW_STEPS = [
  { id: "acquisisce", icon: "Eye", label: "Acquisisce", sub: "Vede e ascolta ciò che accade" },
  { id: "comprende", icon: "Brain", label: "Comprende", sub: "Legge e interpreta" },
  { id: "cataloga", icon: "Database", label: "Cataloga", sub: "Classifica e organizza" },
  { id: "collega", icon: "Cpu", label: "Collega", sub: "Coinvolge i pilastri" },
  { id: "controlla", icon: "ShieldCheck", label: "Controlla", sub: "Verifica e segnala" },
  { id: "prevede", icon: "Activity", label: "Prevede", sub: "Analizza e anticipa" },
  { id: "propone", icon: "Dna", label: "Propone", sub: "Suggerisce azioni" },
  { id: "agisce", icon: "Layers", label: "Agisce", sub: "Quando autorizzata" },
  { id: "informa", icon: "Users", label: "Informa", sub: "Tiene sempre aggiornato" },
];
